import type {ReactElement} from 'react';
import {useState} from 'react';
import {Button, ButtonSize, ButtonType} from '@Components/button';
import {ClickableDiv} from '@Components/clickable-div';
import {Icon} from '@Components/icon-v2';
import {IconShape, IconSize, IconType} from '@Components/icon-v2/icon.types';
import {Text, TextSize} from '@Components/text';
import type {SavedLoginAccount} from '@Libraries/login-signup-library';
import {forgetSavedLoginAccounts, isMobileScreen, isPhoneScreen, MAX_SAVED_LOGIN_ACCOUNTS_ON_MOBILE_AND_TABLET} from '@Libraries/login-signup-library';
import useWindowSize from '@/hooks/useWindowSize';
import {useIsRemovingSavedAccounts, useLoginPageSavedAccounts} from '@Components/login-page/login-page.hooks';
import {setSavedAccounts, startRemovingSavedAccounts, stopRemovingSavedAccounts} from '@Components/login-page/login-page-reducer';
import {onAjaxError} from '@Utils/ajax.util';
import {useAppDispatch} from '@/hooks';
import type {SavedAccountsPanelProps} from './saved-accounts-panel.types';
import {SavedAccountsLayout} from './saved-accounts-panel.types';
import {UserAvatar} from '@Components/user-avatar/user-avatar';
import {UserAvatarSize} from '@Components/user-avatar/user-avatar.types';
import {SavedAccountRow} from './components/saved-account-row/saved-account-row';
import {SavedAccountRemovalRow} from './components/saved-account-removal-row/saved-account-removal-row';
import styles from './saved-accounts-panel.module.scss';
import {AuthTestId} from '@/test-ids/auth-test-id';

/**
 * "Login info saved" (§32): the accounts this device remembers. One account is a welcome card,
 * more are a chooser, and "Remove accounts" turns either into a checklist that asks the server to forget
 * the selection. Phone and tablet widths list the most recent three.
 */
export function SavedAccountsPanel({onContinue, onUseAnotherAccount, onAllAccountsRemoved, layout = SavedAccountsLayout.STANDARD}: SavedAccountsPanelProps): ReactElement {
  const dispatch = useAppDispatch();
  const savedAccounts = useLoginPageSavedAccounts();
  const isRemovingAccounts = useIsRemovingSavedAccounts();
  const {windowWidth} = useWindowSize();
  const visibleAccounts = isMobileScreen(windowWidth) ? savedAccounts.slice(0, MAX_SAVED_LOGIN_ACCOUNTS_ON_MOBILE_AND_TABLET) : savedAccounts;
  /** Phones use a smaller back control: a 20px icon in a 40px box. */
  const backIconSize = isPhoneScreen(windowWidth) ? IconSize.SIZE_ICON_20 : IconSize.SIZE_ICON_24;
  const [hashedIdsSelectedForRemoval, setHashedIdsSelectedForRemoval] = useState<string[]>([]);
  const [isForgetting, setIsForgetting] = useState(false);

  const getRootClassName = (rootClassName: string): string => {
    return layout === SavedAccountsLayout.SHEET ? `${rootClassName} ${styles.sheetLayout}` : rootClassName;
  };

  const startRemovingAccounts = (): void => {
    setHashedIdsSelectedForRemoval([]);
    dispatch(startRemovingSavedAccounts());
  };

  const stopRemovingAccounts = (): void => {
    dispatch(stopRemovingSavedAccounts());
  };

  const toggleAccountForRemoval = (account: SavedLoginAccount, isSelected: boolean): void => {
    setHashedIdsSelectedForRemoval((current) => {
      const others = current.filter((hashedId) => hashedId !== account.hashedId);
      return isSelected ? [...others, account.hashedId] : others;
    });
  };

  const isEveryListedAccountSelected = (): boolean => {
    return visibleAccounts.every((account) => hashedIdsSelectedForRemoval.includes(account.hashedId));
  };

  /**
   * Selecting every listed account forgets the whole device list, not just those rows: the phone, tablet and popup
   * lists show only the most recent three, and the older accounts they leave out would otherwise reappear once the
   * listed ones are gone. An empty list asks the server to forget them all.
   *
   * @return the accounts still remembered afterwards
   */
  const forgetSelectedAccounts = async (): Promise<SavedLoginAccount[]> => {
    if (isEveryListedAccountSelected()) {
      await forgetSavedLoginAccounts([]);
      return [];
    }
    await forgetSavedLoginAccounts(hashedIdsSelectedForRemoval);
    return savedAccounts.filter((account) => !hashedIdsSelectedForRemoval.includes(account.hashedId));
  };

  const removeSelectedAccounts = async (): Promise<void> => {
    setIsForgetting(true);
    try {
      const remainingAccounts = await forgetSelectedAccounts();
      dispatch(setSavedAccounts(remainingAccounts));
      dispatch(stopRemovingSavedAccounts());
      if (remainingAccounts.length === 0) {
        onAllAccountsRemoved();
      }
    } catch (e) {
      onAjaxError(e);
    } finally {
      setIsForgetting(false);
    }
  };

  const getRemoveAccountsLink = (): ReactElement => {
    return (
      <ClickableDiv className={styles.removeAccountsLink} data-testid={AuthTestId.REMOVE_ACCOUNTS_LINK} onClick={startRemovingAccounts}>
        <Icon className={styles.removeAccountsIcon} icon="icon-user-x" size={IconSize.SIZE_ICON_16} />
        <Text className={styles.removeAccountsText} size={TextSize.MD} val={window.i18next.t('pmwjs_remove_accounts')} />
      </ClickableDiv>
    );
  };

  const getUseAnotherAccountButton = (): ReactElement => {
    return (
      <Button
        attributes={{'data-testid': AuthTestId.USE_ANOTHER_ACCOUNT_BUTTON}}
        customClasses="-noun"
        isFullWidth
        onClick={onUseAnotherAccount}
        size={ButtonSize.MEDIUM}
        text={window.i18next.t('pmwjs_use_another_account')}
        type={ButtonType.SECONDARY}
      />
    );
  };

  const getSingleAccountCard = (account: SavedLoginAccount): ReactElement => {
    const continueWithAccount = (): void => {
      onContinue(account);
    };

    return (
      <div className={getRootClassName(styles.panel)}>
        <div className={styles.welcome}>
          <Text attributes={{'data-testid': AuthTestId.HEADING_SAVED_ACCOUNTS}} className={styles.title} val={window.i18next.t('pmwjs_welcome_back_name', {name: account.name})} />
          <div className={styles.cardIdentity}>
            <UserAvatar avatar={account.avatar} name={account.name} size={UserAvatarSize.XL} hasNameBeside />
            <Text className={styles.cardEmail} size={TextSize.MD} val={account.email} />
          </div>
        </div>
        <div className={styles.actions}>
          <Button
            attributes={{'data-testid': AuthTestId.SAVED_ACCOUNT_CONTINUE_BUTTON}}
            isFullWidth
            onClick={continueWithAccount}
            size={ButtonSize.MEDIUM}
            text={window.i18next.t('pmwjs_continue')}
            type={ButtonType.PRIMARY}
          />
          {getUseAnotherAccountButton()}
        </div>
        {getRemoveAccountsLink()}
      </div>
    );
  };

  const getAccountChooser = (): ReactElement => {
    return (
      <div className={getRootClassName(styles.panel)}>
        <div className={styles.chooserBody}>
          <div className={styles.chooserHeaderAndList}>
            <div className={styles.chooserHeader}>
              <Text attributes={{'data-testid': AuthTestId.HEADING_SAVED_ACCOUNTS}} className={styles.title} val={window.i18next.t('pmwjs_welcome_back')} />
              <Text className={styles.subtitle} size={TextSize.MD} val={window.i18next.t('pmwjs_saved_accounts_choose_prompt')} />
            </div>
            <div className={styles.accountList}>
              {visibleAccounts.map((account) => {
                return <SavedAccountRow account={account} key={account.hashedId} onChoose={onContinue} />;
              })}
            </div>
          </div>
          {getUseAnotherAccountButton()}
        </div>
        {getRemoveAccountsLink()}
      </div>
    );
  };

  const getRemoveMode = (): ReactElement => {
    return (
      <div className={getRootClassName(styles.removeMode)}>
        <div className={styles.removeHeader}>
          <Icon
            ariaLabel={window.i18next.t('pmwjs_back')}
            className={styles.backIcon}
            icon="icon-arrow-left"
            isUsedAsButton
            onClick={stopRemovingAccounts}
            shape={IconShape.SQUARE}
            size={backIconSize}
            type={IconType.TRANSPARENT}
          />
          <Text attributes={{'data-testid': AuthTestId.HEADING_SAVED_ACCOUNTS}} className={styles.title} val={window.i18next.t('pmwjs_remove_accounts')} />
        </div>
        <div className={styles.accountList}>
          {visibleAccounts.map((account) => {
            return <SavedAccountRemovalRow account={account} isSelected={hashedIdsSelectedForRemoval.includes(account.hashedId)} key={account.hashedId} onToggle={toggleAccountForRemoval} />;
          })}
        </div>
        <Button
          attributes={{'data-testid': AuthTestId.REMOVE_SELECTED_ACCOUNTS_BUTTON}}
          customClasses={`-noun ${styles.removeSelectedButton}`}
          disabled={hashedIdsSelectedForRemoval.length === 0}
          isFullWidth
          isLoading={isForgetting}
          onClick={removeSelectedAccounts}
          size={ButtonSize.MEDIUM}
          text={window.i18next.t('pmwjs_remove_selected_accounts')}
          type={ButtonType.PRIMARY}
        />
      </div>
    );
  };

  if (isRemovingAccounts) {
    return getRemoveMode();
  }
  const [onlyAccount] = visibleAccounts;
  if (onlyAccount !== undefined && visibleAccounts.length === 1) {
    return getSingleAccountCard(onlyAccount);
  }
  return getAccountChooser();
}
